import { buffers } from 'redux-saga';
import {
  actionChannel,
  all,
  call,
  fork,
  put,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';

import { FEATURE_LOADING_TYPES } from 'modules/common/viewer/cadViewer/constants';
import {
  getPartFileByUuid,
  getPartFilesByUuids,
} from 'modules/common/viewer/data/reducer';
import { getActiveFileUUID } from 'modules/viewerPage/data/reducer';

import partService from 'services/partService';

import {
  types,
  featureLoading,
  fetchPartFileGeometricAnalysisData,
  geometricAnalysisQueue,
  partFileGeometricAnalysis,
} from './actions';
import {
  getCachedFeatureData,
  getGeometricAnalysisDataForPartFile,
  getGeometricAnalysisQueue,
  getIsAnyGeometricAnalysisDataFetching,
} from './reducer';
import { processFeatures, processFeedbackItems, processSetups } from './utils';

export default function* root() {
  yield fork(watchGeometricAnalysisQueue);
  yield takeEvery(types.FEATURE_LOADING_REQUEST, watchFeatureLoadingRequest);
  yield takeEvery(
    types.BULK_GEOMETRIC_ANALYSIS_RECEIVE,
    watchReceiveBulkGeometricAnalysisData,
  );
}

function* fetchGeometricAnalysisData(partFileUuid) {
  try {
    yield put(
      fetchPartFileGeometricAnalysisData.init({
        partFileUuid,
      }),
    );

    const { features, feedbackItems, interrogationFiles, setups } = yield call(
      partService.partFiles.geometricAnalysisData.get,
      partFileUuid,
    );

    const partFile = yield select(getPartFileByUuid, partFileUuid);
    let flatFeatures = [];
    let flatFeedbackItems = [];
    if (partFile?.flatFileUuid) {
      const flatFileData = yield call(
        partService.partFiles.geometricAnalysisData.get,
        partFile.flatFileUuid,
      );
      flatFeatures = flatFileData.features;
      flatFeedbackItems = flatFileData.feedbackItems;
    }

    const cachedFeatureData = yield select(getCachedFeatureData);

    const processedFeatures = processFeatures({
      features,
      flatFeatures,
      cachedFeatureData,
    });
    const processedFeedbackItems = processFeedbackItems({
      feedbackItems,
      flatFeedbackItems,
      cachedFeatureData,
    });
    const processedSetups = processSetups(setups);

    yield put(
      fetchPartFileGeometricAnalysisData.success({
        geometricAnalysisData: {
          features: processedFeatures,
          feedbackItems: processedFeedbackItems,
          interrogationFiles,
          setups: processedSetups,
        },
        partFileUuid,
      }),
    );
  } catch (error) {
    console.error('Error in watchFetchGeometricAnalysisData', error);
    yield put(fetchPartFileGeometricAnalysisData.error({ partFileUuid }));
  }
}

function* watchReceiveBulkGeometricAnalysisData(action) {
  const activePartFileUUID = yield select(getActiveFileUUID);

  const partFileUuids = action?.payload?.partFileUuids
    ? [...action.payload.partFileUuids]
    : [];
  if (!partFileUuids.length) {
    return;
  }

  // add child component UUID if starting as active file, because child components are not included in partFile list form tech data package
  // https://paperlessparts.atlassian.net/browse/PP-20471
  if (activePartFileUUID && !partFileUuids.includes(activePartFileUUID)) {
    partFileUuids.push(activePartFileUUID);
  }

  try {
    for (const partFileUuid of partFileUuids) {
      yield put(
        fetchPartFileGeometricAnalysisData.init({
          partFileUuid,
        }),
      );
    }

    const geometricAnalysisDatas = yield call(
      partService.partFiles.bulkGeometricAnalysisData.get,
      partFileUuids,
    );

    const partFiles = yield select(getPartFilesByUuids, partFileUuids);

    const flatPartFileUuids = partFiles
      .filter((partFile) => partFile?.flatFileUuid)
      .map((partFile) => partFile.flatFileUuid);

    let flatGeometricAnalysisDatas = [];

    if (flatPartFileUuids.length) {
      flatGeometricAnalysisDatas = yield call(
        partService.partFiles.bulkGeometricAnalysisData.get,
        flatPartFileUuids,
      );
    }

    for (const {
      features,
      feedbackItems,
      uuid,
    } of flatGeometricAnalysisDatas.values()) {
      const mainFileUuidOfFlatFileUuid = partFiles.find(
        (partFile) => uuid === partFile.flatFileUuid,
      )?.uuid;

      if (mainFileUuidOfFlatFileUuid) {
        const mainFile = geometricAnalysisDatas.find((geoData) => geoData.uuid);
        if (mainFile) {
          mainFile.flatFeatures = features;
          mainFile.flatFeedbackItems = feedbackItems;
        }
      }
    }

    const cachedFeatureData = yield select(getCachedFeatureData);

    for (const {
      features,
      feedbackItems,
      interrogationFiles,
      setups,
      flatFeatures = [],
      flatFeedbackItems = [],
      uuid,
    } of geometricAnalysisDatas.values()) {
      const processedFeatures = processFeatures({
        features,
        flatFeatures,
        cachedFeatureData,
      });
      const processedFeedbackItems = processFeedbackItems({
        feedbackItems,
        flatFeedbackItems,
        cachedFeatureData,
      });
      const processedSetups = processSetups(setups);

      yield put(
        fetchPartFileGeometricAnalysisData.success({
          geometricAnalysisData: {
            features: processedFeatures,
            feedbackItems: processedFeedbackItems,
            interrogationFiles,
            setups: processedSetups,
          },
          partFileUuid: uuid,
        }),
      );
    }
  } catch (error) {
    console.error('Error in watchFetchGeometricAnalysisData', error);
    for (const partFileUuid of partFileUuids) {
      yield put(fetchPartFileGeometricAnalysisData.error({ partFileUuid }));
    }
  }
}

function* popGeometricAnalysisQueue() {
  /*
  Responsible for managing the geometric analysis queue to ensure there is only ever one task being run at a time.
  */
  try {
    const isFetchingGeometricAnalysis = yield select(
      getIsAnyGeometricAnalysisDataFetching,
    );
    if (!isFetchingGeometricAnalysis) {
      const queue = yield select(getGeometricAnalysisQueue);
      if (queue.length) {
        const nextPartFileUuid = queue[0];
        yield call(fetchGeometricAnalysisData, nextPartFileUuid);
        yield put(
          geometricAnalysisQueue.remove({ partFileUuid: nextPartFileUuid }),
        );
      }
    }
  } catch (error) {
    console.error('Error in watchGeometricAnalysisQueue', error);
  }
}

function* watchGeometricAnalysisQueue() {
  //Responsible for managing the partfile cad data queue to ensure there is only ever one task being run at a time.

  const chan = yield actionChannel(
    [
      types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_END,
      types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_START,
    ],
    buffers.expanding(25),
  );
  for (;;) {
    yield take(chan);
    yield call(popGeometricAnalysisQueue);
  }
}

function* watchFeatureLoadingRequest(action) {
  /*
  This function is responsible for fetching additional tessalation data for features and feedback items.

  Long term, this fetching and caching should be the responsibility of @frontend/partviewer.
  */
  try {
    const { payload } = action;
    const { features, partFileUuid, type } = payload;

    const batchSize = 200;
    const featureContentsById = {};
    for (
      let batchCount = 0;
      batchCount < Math.ceil(features.length / batchSize);
      batchCount++
    ) {
      const startInd = batchSize * batchCount;
      const endInd = (batchCount + 1) * batchSize;
      const section = features.slice(startInd, endInd);
      const featureContents = yield all(
        section.map((feature) =>
          fetch(feature.authenticatedUrl).then((response) => response.json()),
        ),
      );
      for (const i in section) {
        featureContentsById[section[i].id] = featureContents[i];
      }
    }

    // update stored features
    const geometricAnalysisData = yield select(
      getGeometricAnalysisDataForPartFile,
      partFileUuid,
    );
    if (geometricAnalysisData) {
      const featureIds = features.map((f) => f.id);
      let nextGeometricAnalysisData;
      if (type === FEATURE_LOADING_TYPES.FEATURE) {
        nextGeometricAnalysisData = {
          ...geometricAnalysisData,
          features: geometricAnalysisData.features.map((feature) => {
            if (featureIds.includes(feature.id)) {
              return {
                ...feature,
                additionalViewerContent:
                  featureContentsById?.[feature.id] ?? {},
                needsLoading: false,
              };
            } else {
              return feature;
            }
          }),
        };
      } else if (type === FEATURE_LOADING_TYPES.FEEDBACK) {
        nextGeometricAnalysisData = {
          ...geometricAnalysisData,
          feedbackItems: geometricAnalysisData.feedbackItems.map(
            (feedbackItem) => {
              if (featureIds.includes(feedbackItem.id)) {
                return {
                  ...feedbackItem,
                  additionalViewerContent:
                    featureContentsById?.[feedbackItem.id] ?? {},
                  needsLoading: false,
                };
              } else {
                return feedbackItem;
              }
            },
          ),
        };
      }

      yield put(
        partFileGeometricAnalysis.receive({
          geometricAnalysisData: nextGeometricAnalysisData,
          partFileUuid,
        }),
      );
    }

    // update cached feature info with success dispatch
    yield put(featureLoading.success({ featureContentsById, type }));
  } catch (error) {
    console.error('Error loading feature contents', error);
    const { features, type } = action.payload;
    if (features) {
      yield put(
        featureLoading.error({
          featureIds: (features || []).map((f) => f.id),
          type,
        }),
      );
    }
  }
}
